// クライアント特化の UI / 動作パラメータ。
// 仕様の根幹 (時間軸・バケット・1日境界) は types/constants.ts。
// ここはダッシュボードの「見せ方」のチューニングだけ置く。

// ----------- チャート -----------

/** バー左スライドアニメの時間 (ms)。 */
export const ROLL_MS = 350;

/** バー間隔 (px)。 */
export const BAR_GAP = 2;

/** 全社ビューで色分けする会社数の上限。超えた分はまとめて「その他」に。 */
export const MAX_COMPANIES = 100;

// ----------- 各社ロスター -----------

/** 同時に表示する顧客行の最大数。古い行は削除される。 */
export const MAX_ROSTER = 14;

/** 1 行あたりに保持する Job インディケータ dot の最大数。 */
export const MAX_DOTS = 12;

/** NEW バッジが表示される時間 (ms)。 */
export const NEW_BADGE_MS = 15_000;

/** Job インディケータの赤 (失敗) が表示され続ける時間 (ms)。 */
export const FAILED_DOT_MS = 5 * 60 * 1000;

/** Job インディケータの緑 / 灰 がフェードアウトするまでの時間 (ms)。 */
export const FADE_TERMINAL_DOT_MS = 2_500;

// ----------- LIVE FEED -----------

/** LIVE FEED に表示する最新イベント数。 */
export const MAX_FEED = 12;

// ----------- シークバー -----------

/** シークバーの解像度 (max 値)。10000 ≒ 0.01% 単位。 */
export const SEEK_MAX = 10_000;

// ----------- LIVE モード -----------

/**
 * LIVE モードでの遅延 (ms)。cursor は常に `Date.now() - LIVE_DELAY_MS` に pin される。
 * BE のポーリング遅延 (5 分) + マージン (1 分) ≒ 6 分。
 */
export const LIVE_DELAY_MS = 6 * 60 * 1000;

/**
 * LIVE モードでの fetchAhead 間隔 (ms)。
 * LIVE_DELAY_MS (= cursor の遅れ) 以内であれば cursor がバッファを追い越さない。
 * spec の「5 分おきに取得して 5+α 分遅れで再生」を素直に実装。
 */
export const LIVE_POLL_MS = 5 * 60 * 1000;

/**
 * loadMeta した結果が「直近のデータ」と判定する閾値 (ms)。
 * meta.lastAt > now - これ なら今日の live モードへ、そうでなければ過去再生モードへ。
 */
export const FRESH_DATA_WINDOW_MS = 24 * 60 * 60 * 1000;

// ----------- API 接続 -----------

/**
 * BE API のベース URL。
 * - ローカル開発: 空文字 (= 同一オリジン relative path)
 * - 本番: esbuild の `--define:` で `https://api.dashboard.example` 等を埋め込む
 */
declare const __API_BASE__: string | undefined;
export const API_BASE: string =
  typeof __API_BASE__ === 'string' ? __API_BASE__ : '';

// ----------- Cognito (任意。本番ビルド時のみ埋め込まれる) -----------

declare const __COGNITO_DOMAIN__: string | undefined;
declare const __COGNITO_CLIENT_ID__: string | undefined;
declare const __COGNITO_REDIRECT_URI__: string | undefined;
declare const __COGNITO_IDP_NAME__: string | undefined;

/** Cognito Hosted UI のドメイン。空なら認証スキップ (ローカル開発)。 */
export const COGNITO_DOMAIN: string =
  typeof __COGNITO_DOMAIN__ === 'string' ? __COGNITO_DOMAIN__ : '';
export const COGNITO_CLIENT_ID: string =
  typeof __COGNITO_CLIENT_ID__ === 'string' ? __COGNITO_CLIENT_ID__ : '';
export const COGNITO_REDIRECT_URI: string =
  typeof __COGNITO_REDIRECT_URI__ === 'string' ? __COGNITO_REDIRECT_URI__ : '';
/**
 * Cognito の identity_provider パラメータ。設定すると Hosted UI を経由せず
 * 指定の外部 IdP に直接リダイレクトする。例: "GoogleWorkspace"
 */
export const COGNITO_IDP_NAME: string =
  typeof __COGNITO_IDP_NAME__ === 'string' ? __COGNITO_IDP_NAME__ : '';

/** Cognito 設定が揃っているか (本番ビルドで define されたか)。 */
export const AUTH_ENABLED: boolean =
  COGNITO_DOMAIN !== '' && COGNITO_CLIENT_ID !== '' && COGNITO_REDIRECT_URI !== '';
